'use client';

import { useCallback, useMemo } from 'react';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import type { ColumnDef } from '@/lib/table-features';
import { useTranslations } from 'next-intl';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { RouteGate } from '@/components/route-gate';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import {
  ArrowRightLeft,
  Download,
  EllipsisVertical,
  PackagePlus,
  Printer,
  Wrench
} from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from '@/i18n/routing';
import type { DataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import { ListingSkeleton } from '@/components/listing-skeleton';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import { useHashParamDialog } from '@/hooks/use-hash-dialog';
import {
  storageLocationKeys,
  storageLocationsApi,
  type StorageLocation
} from '@/features/storage-locations';
import { SHELVES_HASH } from '../constants';
import { shelvesApi } from '../services/shelves.api';
import { useShelves } from '../services/shelves.queries';
import { useDeleteShelf, useUpdateShelf } from '../services/shelves.mutations';
import { ShelfEditDialog } from './shelf-edit-dialog';
import { ShelvesTableActionBar } from './shelves-table-action-bar';
import { ShelfCard } from './shelf-card';
import { ShelfCompactRow } from './shelf-compact-row';
import type { Shelf, ShelvesListParams } from '../types';

function getCapacityBarColor(
  utilization: number,
  status: Shelf['status']
): string {
  if (status === 'maintenance') return 'bg-muted-foreground';
  if (utilization >= 100) return 'bg-red-500';
  if (utilization >= 90) return 'bg-orange-500';
  return 'bg-blue-500';
}

function StatusBadge({ status }: { status: Shelf['status'] }) {
  const t = useTranslations('shelves.status');
  const variants: Record<Shelf['status'], { badge: string; dot: string }> = {
    active: {
      badge: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
      dot: 'bg-blue-500'
    },
    full: {
      badge: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
      dot: 'bg-red-500'
    },
    maintenance: {
      badge: 'bg-muted text-muted-foreground',
      dot: 'bg-gray-400'
    }
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium tracking-wide uppercase',
        variants[status].badge
      )}
    >
      <span
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          variants[status].dot
        )}
      />
      {t(status)}
    </span>
  );
}

interface ShelvesListingProps {
  view: DataView;
}

export function ShelvesListing({ view }: ShelvesListingProps) {
  const t = useTranslations('shelves');
  const tc = useTranslations('common');
  const router = useRouter();

  // Overlay open-state lives in the URL hash (#edit/<id>, #delete/<id>) so
  // overlays are deep-linkable and close on browser Back. Destructure the
  // stable callbacks for use in hook dep arrays (React Compiler rule).
  const {
    open: editOpen,
    param: editId,
    openWith: openEdit,
    onOpenChange: closeEdit
  } = useHashParamDialog(SHELVES_HASH.edit);
  const {
    open: deleteOpen,
    param: deleteId,
    openWith: openDelete,
    onOpenChange: closeDelete
  } = useHashParamDialog(SHELVES_HASH.delete);

  const mapLocationOption = useCallback(
    (loc: StorageLocation) => ({
      value: String(loc.id),
      label: loc.name
    }),
    []
  );

  const locationInfinite = useInfiniteSelect<StorageLocation>({
    queryKey: storageLocationKeys.lists(),
    queryFn: (params) => storageLocationsApi.getAll(params),
    mapOption: mapLocationOption
  });

  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState(
    'shelf_code',
    parseAsString.withDefault('')
  );
  const [urlStatus] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlZone] = useQueryState(
    'zone',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );

  const queryParams = useMemo<ShelvesListParams>(() => {
    const params: ShelvesListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch.length >= 3) params.search = urlSearch;
    if (urlStatus.length > 0) params.status = urlStatus;
    if (urlZone.length > 0) {
      const ids = urlZone.map(Number).filter((n) => n > 0);
      if (ids.length > 0) params.storage_location_id = ids;
    }
    return params;
  }, [urlPage, urlPerPage, urlSearch, urlStatus, urlZone]);

  const {
    data: response,
    isLoading,
    isPlaceholderData
  } = useShelves(queryParams);
  const shelves = useMemo(() => response?.data ?? [], [response?.data]);
  const pagination = response?.pagination;

  // Resolve the hash id param back to the loaded row (row must be on the
  // current page; deep-linking an off-page id simply won't open the overlay).
  const editTarget = editOpen
    ? (shelves.find((s) => String(s.id) === editId) ?? null)
    : null;
  const deleteTargetId =
    deleteOpen && deleteId !== null ? Number(deleteId) : null;

  const deleteShelf = useDeleteShelf();
  const updateShelf = useUpdateShelf();

  const handleDelete = async (id: number) => {
    try {
      await deleteShelf.mutateAsync(id);
      closeDelete(false);
    } catch {
      // Error handling via mutation meta
    }
  };

  const handleMarkMaintenance = useCallback(
    async (shelf: Shelf) => {
      try {
        await updateShelf.mutateAsync({
          id: shelf.id,
          data: { status: 'maintenance' }
        });
      } catch {
        // Error handling via mutation meta
      }
    },
    [updateShelf]
  );

  const columns: ColumnDef<Shelf>[] = useMemo(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40
      },
      {
        id: 'shelf_code',
        accessorKey: 'shelf_code',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('columns.shelfCode')}
          />
        ),
        cell: ({ getValue, column }) => (
          <span className='font-mono text-sm font-bold'>
            <HighlightMatch
              text={getValue() as string}
              query={getColumnSearchTerms(column)}
            />
          </span>
        ),
        enableColumnFilter: true,
        meta: {
          label: t('columns.shelfCode'),
          variant: 'text' as const,
          placeholder: t('searchPlaceholder'),
          permission: 'shelves.column.shelfCode'
        }
      },
      {
        id: 'zone',
        accessorFn: (row) => row.storage_location?.name ?? '',
        header: t('columns.zone'),
        cell: ({ row }) => (
          <span className='text-muted-foreground text-sm'>
            {row.original.storage_location?.name ?? '—'}
          </span>
        ),
        enableColumnFilter: true,
        meta: {
          label: t('columns.zone'),
          variant: 'infiniteMultiSelect' as const,
          permission: 'shelves.column.zone'
        }
      },
      {
        id: 'slot',
        accessorKey: 'slot',
        header: t('columns.slot'),
        cell: ({ getValue }) => (
          <span className='text-sm'>
            {(getValue() as string | null) ?? '—'}
          </span>
        ),
        meta: {
          label: t('columns.slot'),
          permission: 'shelves.column.slot'
        }
      },
      {
        id: 'currentCapacity',
        header: t('columns.currentCapacity'),
        cell: ({ row }) => {
          const s = row.original;
          const barColor = getCapacityBarColor(s.utilization, s.status);
          return (
            <div className='w-36 space-y-1'>
              <p className='text-sm font-medium'>
                {s.current_capacity}
                <span className='text-muted-foreground font-normal'>
                  /{s.max_capacity}
                </span>
              </p>
              <div className='bg-muted h-1.5 w-full overflow-hidden rounded-full'>
                <div
                  className={cn('h-full rounded-full transition-all', barColor)}
                  style={{ width: `${Math.min(s.utilization, 100)}%` }}
                />
              </div>
            </div>
          );
        },
        meta: {
          label: t('columns.currentCapacity'),
          permission: 'shelves.column.currentCapacity'
        }
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('columns.status')} />
        ),
        cell: ({ getValue }) => (
          <StatusBadge status={getValue() as Shelf['status']} />
        ),
        enableColumnFilter: true,
        meta: {
          label: t('columns.status'),
          variant: 'multiSelect' as const,
          options: [
            { value: 'active', label: t('status.active') },
            { value: 'full', label: t('status.full') },
            { value: 'maintenance', label: t('status.maintenance') }
          ],
          permission: 'shelves.column.status'
        }
      },
      {
        id: 'actions',
        header: t('columns.actions'),
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                <EllipsisVertical className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                onClick={() => openEdit(String(row.original.id))}
              >
                {t('edit')}
              </DropdownMenuItem>
              {/* <DropdownMenuItem
                onClick={() => toast.info(t('transferComingSoon'))}
              >
                <ArrowRightLeft className='me-2 h-3.5 w-3.5' />
                {t('transferItems')}
              </DropdownMenuItem> */}
              {row.original.status !== 'maintenance' && (
                <DropdownMenuItem
                  onClick={() => handleMarkMaintenance(row.original)}
                >
                  <Wrench className='me-2 h-3.5 w-3.5' />
                  {t('markMaintenance')}
                </DropdownMenuItem>
              )}
              {row.original.qr_code && (
                <RouteGate route='/dashboard/warehouse-assignment'>
                  <DropdownMenuItem
                    onClick={() =>
                      router.push(
                        `/dashboard/warehouse-assignment?shelf_qr=${row.original.qr_code}`
                      )
                    }
                  >
                    <PackagePlus className='me-2 h-3.5 w-3.5' />
                    {t('assignBox')}
                  </DropdownMenuItem>
                </RouteGate>
              )}
              <DropdownMenuItem
                className='text-destructive focus:text-destructive'
                onClick={() => openDelete(String(row.original.id))}
              >
                {t('delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        enableSorting: false,
        enableHiding: false
      }
    ],
    [t, handleMarkMaintenance, router, openEdit, openDelete]
  );

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable({
    data: shelves,
    columns: filteredColumns,
    pageKey: 'shelves',
    pageCount: pagination?.totalPages ?? 0
  });

  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('columns.shelfCode'), value: 'shelf_code' },
      { label: t('columns.status'), value: 'status' },
      { label: t('columns.currentCapacity'), value: 'current_capacity' }
    ],
    [t]
  );

  return (
    <div className='space-y-4'>
      {/* Toolbar */}
      <DataTableToolbar
        table={table}
        viewFilterPermission='shelves.view_filter'
        infiniteConfigs={{ zone: locationInfinite }}
      >
        {view !== 'table' && (
          <SortControl
            options={sortOptions}
            sorting={tableState.sorting}
            onSortingChange={(s) => table.setSorting(s)}
          />
        )}
        {/* <Button
          variant='outline'
          size='icon'
          aria-label={tc('download')}
          onClick={async () => {
            try {
              await shelvesApi.exportCsv();
              toast.success(t('exportSuccess'));
            } catch {
              toast.error(t('exportError'));
            }
          }}
        >
          <Download className='h-4 w-4' />
        </Button>
        <Button
          variant='outline'
          size='icon'
          aria-label={t('print')}
          onClick={() => window.print()}
        >
          <Printer className='h-4 w-4' />
        </Button> */}
      </DataTableToolbar>

      {/* Data */}
      {isLoading ? (
        <ListingSkeleton view={view} />
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {shelves.length === 0 ? (
              <p className='text-muted-foreground col-span-full py-8 text-center'>
                {t('noShelves')}
              </p>
            ) : (
              shelves.map((shelf) => (
                <ShelfCard
                  key={shelf.id}
                  shelf={shelf}
                  onEdit={(s) => openEdit(String(s.id))}
                  onDelete={(s) => openDelete(String(s.id))}
                  onMarkMaintenance={handleMarkMaintenance}
                />
              ))
            )}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {shelves.length === 0 ? (
              <p className='text-muted-foreground py-8 text-center'>
                {t('noShelves')}
              </p>
            ) : (
              shelves.map((shelf) => (
                <ShelfCompactRow
                  key={shelf.id}
                  shelf={shelf}
                  onEdit={(s) => openEdit(String(s.id))}
                  onDelete={(s) => openDelete(String(s.id))}
                  onMarkMaintenance={handleMarkMaintenance}
                />
              ))
            )}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <DataTable
            table={table}
            actionBar={<ShelvesTableActionBar table={table} />}
          >
            {shelves.length === 0 && !isLoading && (
              <div className='text-muted-foreground py-8 text-center'>
                {t('noShelves')}
              </div>
            )}
          </DataTable>
        </div>
      )}

      <ShelfEditDialog
        shelf={editOpen ? editTarget : null}
        onOpenChange={closeEdit}
      />

      <AlertDialog open={deleteOpen} onOpenChange={closeDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='flex justify-end gap-3'>
            <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTargetId && handleDelete(deleteTargetId)}
              disabled={deleteShelf.isPending}
              className='bg-destructive hover:bg-destructive/90'
            >
              {deleteShelf.isPending ? '...' : t('delete')}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
